import { useEffect } from "react"
import {
  DEFAULT_OG_IMAGE,
  SEARCH_CONSOLE_VERIFICATION,
  SEO_KEYWORDS,
  SITE_NAME,
  SITE_URL,
  SUPPORTED_LOCALES,
} from "./seoConfig"

interface SEOProps {
  title: string
  description: string
  path?: string
  image?: string
  siteName?: string
  keywords?: string[]
  noindex?: boolean
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>
}

const upsertMeta = (selector: string, attrs: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null

  if (!element) {
    element = document.createElement("meta")
    document.head.appendChild(element)
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element?.setAttribute(key, value)
  })
}

const upsertLink = (selector: string, attrs: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null

  if (!element) {
    element = document.createElement("link")
    document.head.appendChild(element)
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element?.setAttribute(key, value)
  })
}

export function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  siteName = SITE_NAME,
  keywords = SEO_KEYWORDS,
  noindex = false,
  structuredData,
}: SEOProps) {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`
    const imageUrl = image.startsWith("http") ? image : `${SITE_URL}${image}`

    document.title = `${title} | ${siteName}`

    upsertMeta("meta[name='description']", { name: "description", content: description })
    upsertMeta("meta[name='keywords']", { name: "keywords", content: keywords.join(", ") })
    upsertMeta("meta[name='robots']", { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow" })

    upsertMeta("meta[property='og:type']", { property: "og:type", content: "website" })
    upsertMeta("meta[property='og:site_name']", { property: "og:site_name", content: siteName })
    upsertMeta("meta[property='og:title']", { property: "og:title", content: title })
    upsertMeta("meta[property='og:description']", { property: "og:description", content: description })
    upsertMeta("meta[property='og:image']", { property: "og:image", content: imageUrl })
    upsertMeta("meta[property='og:url']", { property: "og:url", content: canonical })
    upsertMeta("meta[property='og:locale']", { property: "og:locale", content: "pt_BR" })

    upsertMeta("meta[name='twitter:card']", { name: "twitter:card", content: "summary_large_image" })
    upsertMeta("meta[name='twitter:title']", { name: "twitter:title", content: title })
    upsertMeta("meta[name='twitter:description']", { name: "twitter:description", content: description })
    upsertMeta("meta[name='twitter:image']", { name: "twitter:image", content: imageUrl })

    if (SEARCH_CONSOLE_VERIFICATION) {
      upsertMeta("meta[name='google-site-verification']", {
        name: "google-site-verification",
        content: SEARCH_CONSOLE_VERIFICATION,
      })
    }

    upsertLink("link[rel='canonical']", { rel: "canonical", href: canonical })

    SUPPORTED_LOCALES.forEach((locale) => {
      upsertLink(`link[rel='alternate'][hreflang='${locale}']`, {
        rel: "alternate",
        hrefLang: locale,
        href: canonical,
      })
    })

    upsertLink("link[rel='alternate'][hreflang='x-default']", {
      rel: "alternate",
      hrefLang: "x-default",
      href: canonical,
    })

    const schemaScriptId = "mestre-da-comanda-seo-schema"

    const currentScript = document.getElementById(schemaScriptId)

    if (currentScript) {
      currentScript.remove()
    }

    if (structuredData) {
      const script = document.createElement("script")
      script.id = schemaScriptId
      script.type = "application/ld+json"
      script.text = JSON.stringify(structuredData)
      document.head.appendChild(script)
    }
  }, [title, description, path, image, siteName, keywords, noindex, structuredData])

  return null
}
